import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { format, formatDistanceToNow } from 'date-fns';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { getAvatarProps } from '../../../../models/AvatarUtils';
import { getDisplayName } from '../../../../models/NameUtils';
import { useContact } from '../../../../queries/Contact/useContact';
import { useContactKeepInTouchQuery } from '../../../../queries/Contact/useContactKeepInTouchQuery';
import { Avatar } from '../../../components-v2/Avatar';
import { Skeleton } from '../../../components-v2/Skeleton';
import { THEME } from '../../../components-v2/Theme/theme';

interface IProps {
	contact: Api.IContact | null;
}

function formatDate(value: string | Date | undefined): string | null {
	if (!value) {
		return null;
	}
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return null;
	}
	return `${format(date, 'MMM d, yyyy')} (${formatDistanceToNow(date, { addSuffix: true })})`;
}

export const FocusContactPanel: React.FC<IProps> = ({ contact }) => {
	const contactId = contact?.id ?? '';
	const contactQuery = useContact({ contactId, enabled: !!contactId, allowArchived: true });
	const keepInTouchQuery = useContactKeepInTouchQuery({ contactId, enabled: !!contactId });

	if (!contact) {
		return (
			<aside className={css(styleSheet.panel)}>
				<span className={css(styleSheet.emptyTitle)}>No contact linked</span>
				<span className={css(styleSheet.muted)}>
					Link a contact to this action item and their details will show up here while you work.
				</span>
			</aside>
		);
	}

	const full = contactQuery.data ?? contact;
	const phone = full.phoneNumbers?.[0]?.value;
	const email = full.primaryEmail?.value;
	const lastCommunication = formatDate(full.communicationMetadata?.lastCommunicationDate);
	const lastInteraction = formatDate(
		full.relationshipHealth?.userLastInteractionDate ?? full.relationshipHealth?.lastInteractionDate
	);
	const keepInTouch = keepInTouchQuery.data;

	return (
		<aside className={css(styleSheet.panel)} data-testid='focus-contact-panel'>
			<div className={css(styleSheet.identity)}>
				<Avatar styles={[styleSheet.avatar]} {...getAvatarProps(full)} />
				<div className={css(styleSheet.identityText)}>
					<Link to={`/people/${full.id}`} className={css(styleSheet.name)}>
						{getDisplayName(full)}
					</Link>
					{full.jobTitle || full.companyName ? (
						<span className={css(styleSheet.muted)}>
							{[full.jobTitle, full.companyName].filter(Boolean).join(' at ')}
						</span>
					) : null}
				</div>
			</div>
			{contactQuery.isPending ? <Skeleton styles={[styleSheet.skeleton]} /> : null}
			<dl className={css(styleSheet.facts)}>
				{email ? (
					<>
						<dt className={css(styleSheet.factLabel)}>Email</dt>
						<dd className={css(styleSheet.factValue)}>
							<a href={`mailto:${email}`}>{email}</a>
						</dd>
					</>
				) : null}
				{phone ? (
					<>
						<dt className={css(styleSheet.factLabel)}>Phone</dt>
						<dd className={css(styleSheet.factValue)}>
							<a href={`tel:${phone}`}>{phone}</a>
						</dd>
					</>
				) : null}
				{lastCommunication ? (
					<>
						<dt className={css(styleSheet.factLabel)}>Last email</dt>
						<dd className={css(styleSheet.factValue)}>{lastCommunication}</dd>
					</>
				) : null}
				{lastInteraction ? (
					<>
						<dt className={css(styleSheet.factLabel)}>Last interaction</dt>
						<dd className={css(styleSheet.factValue)}>{lastInteraction}</dd>
					</>
				) : null}
				{keepInTouch?.frequency ? (
					<>
						<dt className={css(styleSheet.factLabel)}>Keep in touch</dt>
						<dd className={css(styleSheet.factValue)}>Every {keepInTouch.frequency} days</dd>
					</>
				) : null}
			</dl>
			{full.tagsWithAlerts?.length ? (
				<span className={css(styleSheet.muted)}>Tags: {full.tagsWithAlerts.join(', ')}</span>
			) : null}
			{full.keyFacts?.length ? (
				<ul className={css(styleSheet.keyFacts)}>
					{full.keyFacts.slice(0, 5).map(fact => (
						<li key={fact}>{fact}</li>
					))}
				</ul>
			) : null}
		</aside>
	);
};

const styleSheet = StyleSheet.create({
	panel: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		padding: THEME.spacing.md,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.main,
		minWidth: 0,
		height: '100%',
		boxSizing: 'border-box',
		overflowY: 'auto',
	},
	identity: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	identityText: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
	},
	avatar: {
		width: 44,
		height: 44,
	},
	name: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	muted: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	emptyTitle: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	skeleton: {
		height: 60,
	},
	facts: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr',
		gap: `${THEME.spacing.xs}px ${THEME.spacing.md}px`,
		margin: 0,
	},
	factLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		textTransform: 'uppercase',
		letterSpacing: 0.4,
		alignSelf: 'center',
	},
	factValue: {
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		...THEME.common.truncate,
	},
	keyFacts: {
		margin: 0,
		paddingLeft: THEME.spacing.md,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.main,
	},
});
